// Locations — list view backed by /admin/locations. Filter by `type`
// (city/airport/station/region/country) and `countryCode`.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { COUNTRIES } from '@drivecars/shared';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  PageHeader,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import {
  fetchAdminLocationCountries,
  fetchAdminLocations,
  type AdminLocationRow,
  type AdminLocationsListParams,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type TypeFilter = NonNullable<AdminLocationsListParams['type']>;

const TYPES: Array<{ key: TypeFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'country', label: 'Countries' },
  { key: 'region', label: 'Regions' },
  { key: 'city', label: 'Cities' },
  { key: 'airport', label: 'Airports' },
  { key: 'station', label: 'Stations' },
];

// Country display name for a lowercase ISO2 code; falls back to the code.
const countryName = (code: string) =>
  COUNTRIES.find((c) => c.code === code)?.name ?? code.toUpperCase();

const COLUMNS: DataTableColumn<AdminLocationRow>[] = [
  {
    key: 'name',
    h: 'Location',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.name ?? r.slug}</div>
        <div className="truncate font-mono text-[11.5px] text-[color:var(--bo-mute)]">
          {r.slug}
        </div>
      </div>
    ),
  },
  {
    key: 'type',
    h: 'Type',
    cell: (r) => (
      <span className="text-[12px] capitalize text-[color:var(--bo-ink)]">{r.type}</span>
    ),
  },
  {
    key: 'country',
    h: 'Country',
    cell: (r) =>
      r.countryCode ? (
        <span className="font-mono text-[12px] uppercase">{r.countryCode}</span>
      ) : (
        '—'
      ),
  },
  {
    key: 'topRank',
    h: 'Top rank',
    cell: (r) =>
      r.topRank != null ? (
        <span className="rounded-full bg-[color:var(--bo-ink)]/10 px-2 py-0.5 font-mono text-[11.5px] font-semibold">
          #{r.topRank}
        </span>
      ) : (
        '—'
      ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
];

export function Locations() {
  const [type, setType] = useState<TypeFilter>('all');
  const [country, setCountry] = useState<string>('');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [type, country, q, setPage]);

  // Live country facet — the filter offers exactly the countries that have
  // locations (with counts), not a hardcoded list.
  const countriesQ = useQuery({
    queryKey: ['admin', 'locations', 'countries'],
    queryFn: ({ signal }) => fetchAdminLocationCountries(signal),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'locations', { type, country, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminLocations(
        {
          type,
          countryCode: country || undefined,
          q: q.trim() || undefined,
          page,
          limit,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const navigate = useNavigate();
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Operate"
        title="Locations"
        sub={`${total.toLocaleString()} locations${type === 'all' ? '' : ` · ${type}`}${
          country ? ` · ${countryName(country)}` : ''
        }.`}
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load locations: {(error as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search slug or name…" />
        {TYPES.map((t) => (
          <ToolChip key={t.key} active={type === t.key} onClick={() => setType(t.key)}>
            {t.label}
          </ToolChip>
        ))}
        <select
          aria-label="Filter by country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="h-8 rounded-lg border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)] focus-visible:ring-2 focus-visible:ring-[color:var(--bo-accent)]"
        >
          <option value="">
            {countriesQ.isLoading ? 'Loading countries…' : 'All countries'}
          </option>
          {(countriesQ.data?.countries ?? []).map((cf) => (
            <option key={cf.code} value={cf.code}>
              {countryName(cf.code)} ({cf.count})
            </option>
          ))}
        </select>
      </Toolbar>
      <DataTable
        columns={COLUMNS}
        rows={rows}
        onRowClick={(r) => void navigate({ to: `/locations/${r.id}` as never })}
        emptyLabel={isLoading ? 'Loading…' : 'No matching locations.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}
